import {ComponentStore} from '@ngrx/component-store';
import {MetricsStoreState} from './metrics';
import {Injectable} from '@angular/core';
import {mergeMap, Observable, scan, Subscription, switchMap, withLatestFrom} from 'rxjs';
import {AppState} from '../store';
import {Store} from '@ngrx/store';
import {Actions, ofType} from '@ngrx/effects';
import {QuickDashboardService} from '../../services/quick-dashboard.service';
import {QuickDashboardMetric} from '../../interfaces/quick-dashboard';
import {filter, map, tap} from 'rxjs/operators';
import {TypedAction} from '@ngrx/store/src/models';
import {initSiteSuccess} from '../init/init.actions';
import {Site} from '../../interfaces/site';
import {selectSite} from '../init/init.selectors';
import {Serializer} from '../../interfaces/serializer';
import {ReportStore} from '../report/report.store';
import {AdRequestOptions} from '../../interfaces/ad-reports';

@Injectable()
export class MetricsStore extends ComponentStore<MetricsStoreState> {
  private readonly _dataSet$: Observable<Serializer<Site>> = this._store.select(selectSite);

  public readonly data$: Observable<{ [key: string]: QuickDashboardMetric }> = this.select((state: MetricsStoreState): { [key: string]: QuickDashboardMetric } => state.data);
  public readonly loading$: Observable<boolean> = this.select((state: MetricsStoreState): boolean => state.loading);

  public readonly load: (slugs: Array<string>) => void = this.effect((params$: Observable<Array<string>>) => params$.pipe(
    tap((slugs: Array<string>): void => this.patchState({ slugs: slugs, loading: true })),
    switchMap((slugs: Array<string>): Array<string> => slugs),
    withLatestFrom(this._reportStore.requestOptions$, this._dataSet$),
    mergeMap(([slug, options, dataSet]: [string, Partial<AdRequestOptions>, Serializer<Site>]): Observable<{ key: string, response: QuickDashboardMetric }> => this._service.getMetric(dataSet, slug, options).pipe(
      map((response: QuickDashboardMetric): { key: string, response: QuickDashboardMetric } => ({ key: slug, response: response }))
    )),
    scan((values: { [key: string]: QuickDashboardMetric }, value: { key: string, response: QuickDashboardMetric }): { [key: string]: QuickDashboardMetric } => {
      values[value.key] = value.response

      return values;
    }, {}),
    withLatestFrom(params$),
    filter(([values, slugs]: [{ [key: string]: QuickDashboardMetric }, Array<string> ]): boolean => Object.keys(values).length === slugs.length),
    tap(([values, slugs]: [{ [key: string]: QuickDashboardMetric }, Array<string> ]): void => {
      this.patchState({
        data: values,
        loading: false
      });
    }),
  ));

  private readonly _reloadActions$: Subscription = this.effect((): Observable<[Partial<AdRequestOptions>, MetricsStoreState]> => this._reportStore.requestOptionsChanges$.pipe(
    withLatestFrom(this.state$),
    tap(([_, state]: [Partial<AdRequestOptions>, MetricsStoreState]): void => this.load(state.slugs))
  ));

  private readonly _selectSite$: Subscription = this.effect((): Observable<[TypedAction<string>, MetricsStoreState]> => this._actions$.pipe(
    ofType(initSiteSuccess),
    withLatestFrom(this.state$),
    tap(([_, state]: [TypedAction<string>, MetricsStoreState]): void =>  {
      this.patchState({
        data: {}
      });

      this.load(state.slugs);
    })
  ));

  constructor(
    private readonly _reportStore: ReportStore,
    private readonly _store: Store<AppState>,
    private readonly _actions$: Actions,
    private readonly _service: QuickDashboardService
  ) {
    super({
      data: {},
      slugs: [],
      loading: false
    });
  }

}
